import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigate } from 'react-router-dom';
import ActionLink from '../../../components/actionLink/ActionLink';

import { ImIdVerificationStatuses } from '../../imIdVerificationPending/ImIdVerificationPending';
import Loading from '../../../components/loading/Loading';
import type { AccountInfo } from '../../../utils/storage';
import { getAccountId, getPartner } from '../../../utils/storage';
import { verifyPartnerAccount } from '../../../services/registration';
import { handleApiError } from '../../../utils/apiError';
import {
    readAccountId,
    redirectToPartnerOrConditions,
    resolvePartnerInfo,
    resolvePartnerRedirectUrl,
} from '../../../utils/partnerRedirectFlow';
import useLogger from '../../../hooks/useLogger';
import { IMID_VERIFICATION_ACTIONS, LOG_STATUSES } from '../../../logging/LoggingConstants';

interface ActionContentProps {
    status: ImIdVerificationStatuses;
    tryAgainUrl: string;
    account?: AccountInfo | null;
}

const SUCCESS_CONTINUE_DELAY_MS = 3000;

const ActionContent: React.FC<ActionContentProps> = ({ status, tryAgainUrl, account }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { log } = useLogger();
    const partnerRedirectStartedRef = useRef(false);

    useEffect(() => {
        if (status !== ImIdVerificationStatuses.SUCCESS) {
            return;
        }

        const partner = getPartner();
        const partnerRedirectUrl = resolvePartnerRedirectUrl(account, partner);
        const accountId = readAccountId(account) ?? getAccountId() ?? undefined;

        if (partnerRedirectUrl && accountId !== undefined) {
            if (partnerRedirectStartedRef.current) {
                return;
            }
            partnerRedirectStartedRef.current = true;

            verifyPartnerAccount(accountId)
                .then((res) => {
                    log(IMID_VERIFICATION_ACTIONS.IMID_VERIFY_PARTNER, LOG_STATUSES.SUCCESS);
                    if (res?.success) {
                        redirectToPartnerOrConditions(
                            navigate,
                            partnerRedirectUrl,
                            resolvePartnerInfo(account, partner)
                        );
                    } else {
                        navigate('/conditions');
                    }
                })
                .catch((err) => {
                    handleApiError(err, navigate);
                });
            return;
        }

        const timer = setTimeout(() => {
            navigate('/conditions');
        }, SUCCESS_CONTINUE_DELAY_MS);
        return () => clearTimeout(timer);
    }, [status, account, navigate, log]);

    switch (status) {
        case ImIdVerificationStatuses.SUCCESS:
            return <Loading size={64} className="m-4" />;

        default:
            return (
                <>
                    <ActionLink to={tryAgainUrl} className="lg:min-w-[21rem]">
                        {status === ImIdVerificationStatuses.PHONE_NOT_CONNECTED
                            ? t('imIdVerification.changePhoneNumber')
                            : [
                                    ImIdVerificationStatuses.INCORRECT_SSN,
                                    ImIdVerificationStatuses.REQUEST_CANCELLED,
                                ].includes(status)
                              ? t('imIdVerification.tryAgain')
                              : t('imIdVerification.homepage')}
                    </ActionLink>
                    <ActionLink to="/sumsub" className="lg:min-w-[21rem]" variant="outlined">
                        {t('imIdVerification.biometric')}
                    </ActionLink>
                </>
            );
    }
};

export default ActionContent;
